import {
  Box,
  Button,
  Checkbox,
  FormControl,
  FormControlLabel,
  FormLabel,
  Radio,
  RadioGroup,
  TextField,
  Typography
} from "@mui/material";
import React from "react";
import produce from "immer";
import startCase from "lodash/startCase";
import { useDispatch } from "react-redux";

import { resourceCentreActions } from "../../../../actions";
import HtmlTooltip from "../../../../components/HtmlTooltip";
import useCurrentResourceCentre from "../../../../hooks/useCurrentResourceCentre";

export enum MEDICINE_COMPONENT {
  /**
   * Product Medication Component.
   * Shows products available in stocks
   */
  PRODUCT_MEDICATION = "productMedication",
  /**
   * Medication Component.
   * Shows generic medication from master data
   */
  MEDICATION = "medication"
}

export const GeneralIpdSettings = (): React.ReactNode => {
  const dispatch = useDispatch();
  const resourceCentre = useCurrentResourceCentre();

  // local states
  const [startIpdFrom, setStartIpdFrom] = React.useState(0);

  // use effects
  React.useEffect(() => {
    setStartIpdFrom(resourceCentre.settings.ipdSettings?.startIpdNumberFrom || 0);
  }, [resourceCentre]);

  return (
    <Box sx={{ px: 2, display: "flex", flexDirection: "column", gap: 2 }}>
      <FormControlLabel
        label="Include letterhead on print"
        control={
          <Checkbox
            checked={Boolean(
              resourceCentre.settings.printSettings.ipdPrintSettings.includeLetterhead
            )}
            onChange={(_, checked) => {
              const newRc = produce(resourceCentre, (draft) => {
                draft.settings.printSettings.ipdPrintSettings.includeLetterhead = checked;
              });
              dispatch(resourceCentreActions.putResourceCentre(newRc));
            }}
          />
        }
      />

      <FormControl>
        <FormLabel>
          <Typography>Medication Component</Typography>{" "}
        </FormLabel>
        <RadioGroup
          row
          value={resourceCentre.settings.ipdSettings.medicationComponent}
          onChange={(event) => {
            const newRc = produce(resourceCentre, (draft) => {
              draft.settings.ipdSettings.medicationComponent = event.target
                .value as MEDICINE_COMPONENT;
            });
            dispatch(resourceCentreActions.putResourceCentre(newRc));
          }}
        >
          <FormControlLabel
            value={MEDICINE_COMPONENT.MEDICATION}
            control={<Radio />}
            label={
              <Box display="flex" gap={1} alignItems="center">
                <span>{startCase(MEDICINE_COMPONENT.MEDICATION)}</span>
                <HtmlTooltip title="" description="Shows generic medicines." />
              </Box>
            }
          />
          <FormControlLabel
            value={MEDICINE_COMPONENT.PRODUCT_MEDICATION}
            control={<Radio />}
            label={
              <Box display="flex" gap={1} alignItems="center">
                <span>{startCase(MEDICINE_COMPONENT.PRODUCT_MEDICATION)}</span>
                <HtmlTooltip
                  title=""
                  description="Shows products from stock, it will also create bill request form when prescribed."
                />
              </Box>
            }
          />
        </RadioGroup>
      </FormControl>

      <Box sx={{ display: "flex", alignItems: "center", flexWrap: "wrap", gap: 2 }}>
        <TextField
          sx={{ flex: 1, minWidth: "140px" }}
          label="Start Ipd Number From"
          slotProps={{
            inputLabel: { shrink: true }
          }}
          value={startIpdFrom}
          variant="outlined"
          onChange={(e) => setStartIpdFrom(Number(e.target.value))}
        />
        <Button
          variant="contained"
          onClick={() => {
            const newRc = produce(resourceCentre, (draft) => {
              draft.settings.ipdSettings.startIpdNumberFrom = startIpdFrom;
            });
            dispatch(resourceCentreActions.putResourceCentre(newRc));
          }}
        >
          Update
        </Button>
      </Box>
    </Box>
  );
};
